import { View } from "react-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "./use-theme";
import IconButton from "../components/IconButton";
import Steps, { StepsConfig } from "../ui_atoms/Steps";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { BUBBLE_ANIMATION } from "../components/Bubble";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type SignupSigninStackTitleProps = {
    currentStepIndex: number;
    goBack?: () => void;
    canGoBack?: () => boolean;
    totalSteps: number;
    onClose?: () => void;
    stepsConfig?: StepsConfig;
    isVisible: boolean;
    disableSafeAreaTop?: boolean;
};

const HEADER_HEIGHT = 64;
const ACTION_WIDTH = 28;

const HeaderComponent = ({
    currentStepIndex,
    goBack,
    canGoBack = () => true,
    totalSteps,
    onClose,
    stepsConfig,
    isVisible,
    disableSafeAreaTop = false,
}: SignupSigninStackTitleProps) => {
    const { marginSize, colors } = useTheme();
    const insets = useSafeAreaInsets();

    // When hosted in a native modal (iOS page sheet), the sheet already starts below the status bar,
    // so the device top inset must not be added again as padding.
    const topInset = disableSafeAreaTop ? 0 : insets.top;

    const iconMarginLeft = marginSize - 3;
    const height = useSharedValue(HEADER_HEIGHT);
    const paddingTop = useSharedValue(0);

    const animatedHeaderStyles = useAnimatedStyle(() => ({
        height: height.value,
        paddingTop: paddingTop.value,
    }));

    useEffect(() => {
        height.value = withTiming(isVisible ? topInset + HEADER_HEIGHT : 0, BUBBLE_ANIMATION);
        paddingTop.value = withTiming(isVisible ? topInset : 0, BUBBLE_ANIMATION);
    }, [height, topInset, isVisible, paddingTop]);

    return (
        <Animated.View
            style={[
                animatedHeaderStyles,
                {
                    backgroundColor: colors.background,
                    flexDirection: "row",
                    alignItems: "center",
                },
            ]}
        >
            {canGoBack() ? (
                <IconButton
                    style={{ marginLeft: iconMarginLeft }}
                    onPress={goBack}
                    icon="back"
                    size={ACTION_WIDTH}
                />
            ) : (
                <View style={{ width: ACTION_WIDTH + iconMarginLeft }} />
            )}
            <View style={{ flex: 1 }} />
            {currentStepIndex < totalSteps && (
                <Steps index={currentStepIndex} count={totalSteps} stepsConfig={stepsConfig} />
            )}
            <View style={{ flex: 1 }} />
            {onClose ? (
                <IconButton
                    style={{ marginRight: iconMarginLeft }}
                    onPress={onClose}
                    icon="close"
                    size={ACTION_WIDTH}
                />
            ) : (
                <View style={{ width: ACTION_WIDTH + iconMarginLeft }} />
            )}
        </Animated.View>
    );
};

type UseStepsHeaderProps<T> = {
    getCurrentIndex?: (routeName: T) => number;
    getTotalSteps?: (routeName: T) => number;
    onClose?: () => void | Promise<void>;
    stepsConfig?: StepsConfig;
    headerHidden?: boolean[]; // map of indexes where the header is hidden
    disableSafeAreaTop?: boolean; // skip the safe-area top inset (e.g. inside a native modal sheet)
};

export const useStepsHeader = <T,>({ headerHidden = [], ...params }: UseStepsHeaderProps<T>) => {
    const [goBack, setGoBack] = useState<() => void>();
    const [canGoBack, setCanGoBack] = useState<() => boolean>();
    const [routesNames, setRoutesNames] = useState<T[]>([]);
    const [routeName, setRouteName] = useState<T>();

    const currentIndex = useMemo(
        () =>
            (routeName &&
                (params?.getCurrentIndex?.(routeName) ?? routesNames.indexOf(routeName))) ||
            0,
        [params, routeName, routesNames],
    );

    const totalSteps = useMemo(
        () => (routeName && params?.getTotalSteps?.(routeName)) ?? routesNames.length,
        [params, routeName, routesNames.length],
    );

    const screenListeners = useCallback(
        (newState: {
            navigation?: {
                goBack: () => void;
                canGoBack: () => true;
                getState: () => { routeNames: T[] };
            };
            route: { name: T };
        }) => ({
            // Update the current step on FOCUS, not in this function's body. `screenListeners` (function
            // form) runs per screen while the navigator renders; during a transition Android re-evaluates
            // the screen being left LAST, which set `routeName` back to the previous step and snapped the
            // header bubble backwards. `focus` fires once, for the screen actually shown, so the bubble
            // advances cleanly on every platform.
            focus: () => {
                setGoBack(() => newState.navigation?.goBack);
                setCanGoBack(() => newState.navigation?.canGoBack);
                setRoutesNames(newState.navigation?.getState().routeNames || []);
                setRouteName(newState.route.name);
            },
        }),
        [],
    ) as never;

    const headerComponent = useMemo(
        () => (
            <HeaderComponent
                totalSteps={totalSteps}
                currentStepIndex={currentIndex}
                canGoBack={canGoBack}
                goBack={() => {
                    if (canGoBack && !canGoBack()) {
                        void params?.onClose?.();
                    } else if (goBack) {
                        goBack();
                    }
                }}
                onClose={params.onClose ? () => void params?.onClose?.() : undefined}
                stepsConfig={params?.stepsConfig}
                isVisible={!headerHidden[currentIndex]}
                disableSafeAreaTop={params.disableSafeAreaTop}
            />
        ),
        [totalSteps, currentIndex, goBack, params, headerHidden, canGoBack],
    );

    return { headerComponent, screenListeners };
};
